import { useState } from "react";
import api, { formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import { Upload, X, ArrowUp, ArrowDown, ImagePlus } from "lucide-react";

export default function PhotosManager({ images, onChange, label = "PHOTOS", testIdPrefix = "photos" }) {
    const [url, setUrl] = useState("");
    const [uploading, setUploading] = useState(false);

    const addUrl = () => {
        const trimmed = url.trim();
        if (!trimmed) return;
        onChange([...(images || []), trimmed]);
        setUrl("");
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const absolute = `${API.replace(/\/api$/, "")}${data.url}`;
            onChange([...(images || []), absolute]);
            toast.success("Photo uploaded.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

    const move = (idx, dir) => {
        const next = [...images];
        const swap = idx + dir;
        if (swap < 0 || swap >= next.length) return;
        [next[idx], next[swap]] = [next[swap], next[idx]];
        onChange(next);
    };

    return (
        <div data-testid={`${testIdPrefix}-manager`} className="border border-border">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/30 px-6 py-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber flex items-center gap-2">
                    <ImagePlus className="h-3 w-3" />
                    &gt; {label} ({images?.length || 0})
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        data-testid={`${testIdPrefix}-url-input`}
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
                        placeholder="paste image URL"
                        className="border border-border bg-card px-3 py-1.5 font-mono text-xs outline-none focus:border-phosphor-amber w-56"
                    />
                    <button
                        type="button"
                        data-testid={`${testIdPrefix}-url-add`}
                        onClick={addUrl}
                        className="border border-border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                    >
                        + URL
                    </button>
                    <label className="inline-flex items-center gap-2 border border-phosphor-amber px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber cursor-pointer hover:bg-phosphor-amber hover:text-background transition-colors">
                        <Upload className="h-3 w-3" />
                        {uploading ? "Uploading…" : "Upload"}
                        <input data-testid={`${testIdPrefix}-file-input`} type="file" accept="image/*" onChange={upload} className="hidden" disabled={uploading} />
                    </label>
                </div>
            </div>

            {(!images || images.length === 0) ? (
                <div className="p-6 font-mono text-xs text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>
                    &gt; No photos yet. Upload or paste a URL above.
                </div>
            ) : (
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                    {images.map((src, idx) => (
                        <div
                            key={`${src}-${idx}`}
                            data-testid={`${testIdPrefix}-tile-${idx}`}
                            className="relative border border-border scanlines group"
                        >
                            <img src={src} alt="" className="w-full h-32 object-cover" />
                            {idx === 0 && label === "PHOTOS" && (
                                <span className="absolute top-1 left-1 border border-phosphor-amber bg-background/85 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-phosphor-amber">
                                    Cover
                                </span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-background/85 border-t border-border px-2 py-1.5">
                                <span className="font-mono text-[9px] text-muted-foreground">#{String(idx + 1).padStart(2, "0")}</span>
                                <div className="flex items-center gap-1">
                                    <button type="button" data-testid={`${testIdPrefix}-up-${idx}`} onClick={() => move(idx, -1)} className="p-1 hover:text-phosphor-amber" title="Move earlier">
                                        <ArrowUp className="h-3 w-3" />
                                    </button>
                                    <button type="button" data-testid={`${testIdPrefix}-down-${idx}`} onClick={() => move(idx, 1)} className="p-1 hover:text-phosphor-amber" title="Move later">
                                        <ArrowDown className="h-3 w-3" />
                                    </button>
                                    <button type="button" data-testid={`${testIdPrefix}-remove-${idx}`} onClick={() => remove(idx)} className="p-1 hover:text-destructive" title="Remove">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
